/**
 * Task list landing page constants — single source for labels, column defaults,
 * priority display, and pipeline status chips (5447 / 5780 / 5782).
 */

/** User-facing field labels (API field names unchanged). */
export const TASK_FIELD_LABELS = {
  TASK_NAME: 'Task Name',
  TASK_TYPE: 'Task Type',
  REF: 'Ref',
  STATUS: 'Status',
  PRIORITY: 'Priority',
  DUE_DATE: 'Due Date',
  SUB_CATEGORY: 'Sub Category',
  LOCATION: 'Location',
  ASSERTIONS: 'Assertions',
  PREPARER: 'Preparer',
  APPROVER: 'Approver',
  TB_AMOUNT: 'TB Amount',
  RECON_AMOUNT: 'Recon Amount',
  EXPLAINED_DIFFERENCE: 'Explained Difference',
  UNEXPLAINED_DIFFERENCE: 'Unexplained Difference',
  TRIAL_BALANCE: 'Trial Balance',
  CATEGORY: 'Category',
  TAGS: 'Tags',
  PURPOSE: 'Purpose',
  INSTRUCTION: 'Instruction',
};

/**
 * Default visible column order when no saved user customization exists (5780).
 * Values match `tableHeadersfilter` header `value` keys.
 */
export const DEFAULT_TASKLIST_COLUMNS = [
  { value: 'ref', text: TASK_FIELD_LABELS.REF, visible: true },
  { value: 'description', text: TASK_FIELD_LABELS.TASK_NAME, visible: true },
  { value: 'task_type', text: TASK_FIELD_LABELS.TASK_TYPE, visible: true },
  { value: 'stage1', text: TASK_FIELD_LABELS.STATUS, visible: true },
  { value: 'priority', text: TASK_FIELD_LABELS.PRIORITY, visible: true },
  { value: 'duedate1', text: TASK_FIELD_LABELS.DUE_DATE, visible: true },
  { value: 'subcategory', text: TASK_FIELD_LABELS.SUB_CATEGORY, visible: true },
  { value: 'locationName', text: TASK_FIELD_LABELS.LOCATION, visible: true },
  { value: 'assertions', text: TASK_FIELD_LABELS.ASSERTIONS, visible: true },
  { value: 'preparerids', text: TASK_FIELD_LABELS.PREPARER, visible: true },
  { value: 'approverids', text: TASK_FIELD_LABELS.APPROVER, visible: true },
  {
    value: '__tbtotals',
    text: TASK_FIELD_LABELS.TB_AMOUNT,
    visible: true,
    headerClass: '',
  },
  { value: 'scheduleAmount', text: TASK_FIELD_LABELS.RECON_AMOUNT, visible: true },
  {
    value: 'explainedDifference',
    text: TASK_FIELD_LABELS.EXPLAINED_DIFFERENCE,
    visible: true,
  },
  {
    value: 'difference',
    text: TASK_FIELD_LABELS.UNEXPLAINED_DIFFERENCE,
    visible: true,
  },
];

/** Task detail financial metrics card labels and sub-label text. */
export const TASK_FINANCIAL_METRIC_TEXT = {
  THRESHOLD: 'Threshold',
  ABOVE_THRESHOLD: 'Above Threshold',
  NOT_AVAILABLE: 'N/A',
  EXCEEDS_THRESHOLD_SUFFIX: 'Exceeds Threshold',
};

/**
 * Sub-label under unexplained difference (threshold + optional above-threshold flag).
 * @param {string} thresholdDisplay - formatted threshold value
 * @param {{ aboveThreshold: boolean, isZero: boolean }} flags
 */
export function buildUnexplainedThresholdSubLabel(thresholdDisplay, { aboveThreshold, isZero }) {
  let text = `${TASK_FINANCIAL_METRIC_TEXT.THRESHOLD} · ${thresholdDisplay}`;
  if (aboveThreshold && !isZero) {
    text += ` · ${TASK_FINANCIAL_METRIC_TEXT.ABOVE_THRESHOLD}`;
  }
  return text;
}

/**
 * Tooltip when unexplained difference exceeds the location threshold (task list table).
 * @param {string} thresholdDisplay - formatted threshold value (e.g. "40,000")
 */
export function buildUnexplainedExceedsThresholdTooltip(thresholdDisplay) {
  const threshold =
    thresholdDisplay === TASK_FINANCIAL_METRIC_TEXT.NOT_AVAILABLE
      ? thresholdDisplay
      : `$ ${thresholdDisplay}`;
  return `${TASK_FIELD_LABELS.UNEXPLAINED_DIFFERENCE} ${TASK_FINANCIAL_METRIC_TEXT.EXCEEDS_THRESHOLD_SUFFIX} (${threshold}).`;
}

/** Header value keys for financial amount columns (5780 conditional visibility). */
export const FINANCIAL_COLUMN_VALUES = [
  '__tbtotals',
  'scheduleAmount',
  'explainedDifference',
  'difference',
];

/**
 * Priority display config — icon + label colour; swap icons here without touching consumers.
 * Table spec (5780): High ↑ red, Medium = amber, Low ↓ green; no cell background.
 */
export const PRIORITY_DISPLAY = {
  High: {
    label: 'High',
    icon: 'mdi-arrow-up',
    toggleIcon: 'mdi-chevron-double-up',
    cssModifier: 'high',
  },
  Medium: {
    label: 'Medium',
    icon: 'mdi-equal',
    toggleIcon: 'mdi-equal',
    cssModifier: 'medium',
  },
  Low: {
    label: 'Low',
    icon: 'mdi-arrow-down',
    toggleIcon: 'mdi-chevron-double-down',
    cssModifier: 'low',
  },
};

export const PRIORITY_DISPLAY_NONE = {
  label: '',
  icon: 'mdi-minus',
  toggleIcon: 'mdi-minus',
  cssModifier: 'none',
};

export const PRIORITY_DISPLAY_DEFAULT = PRIORITY_DISPLAY_NONE;

/** Task list persona / role keys (route query `userType`, toolbar, row actions). */
export const TASKLIST_USER_TYPES = {
  MANAGER: 'manager',
  PREPARER: 'preparer',
  APPROVER: 'approver',
};

/** Ordered priority values for toggles and validation. */
export const PRIORITY_VALUES = ['Low', 'Medium', 'High'];

/**
 * Pipeline status chips replacing TaskStageTabs (5447).
 * `key` matches task `stage` filter; `null` = All.
 */
export const PIPELINE_STATUS_CHIPS = [
  {
    key: null,
    label: 'All',
    summaryKey: 'count',
    testId: 'tasklist__pipeline-chip-all',
  },
  {
    key: 0,
    label: 'Pending Submission',
    summaryKey: 'pending',
    testId: 'tasklist__pipeline-chip-pending-submission',
  },
  {
    key: 2,
    label: 'Pending Approval',
    summaryKey: 'pendingapproval',
    testId: 'tasklist__pipeline-chip-pending-approval',
  },
  {
    key: 3,
    label: 'Approved',
    summaryKey: 'approved',
    testId: 'tasklist__pipeline-chip-approved',
  },
];

/** @deprecated Use PIPELINE_STATUS_CHIPS — kept for gradual migration from constants.js TASK_TABS */
export const TASK_TABS = PIPELINE_STATUS_CHIPS;

/**
 * Information Requests status chips (NXTGEN-5875).
 * `key` matches request `stage` filter; `null` = All.
 */
export const INFORMATION_MANAGER_STATUS_CHIPS = [
  {
    key: null,
    label: 'All',
    summaryKey: 'count',
    testId: 'information-requests__pipeline-chip-all',
  },
  {
    key: 0,
    label: 'Pending Submission',
    summaryKey: 'pending',
    testId: 'information-requests__pipeline-chip-pending-submission',
  },
  {
    key: 1,
    label: 'Pending Approval',
    summaryKey: 'pendingapproval',
    testId: 'information-requests__pipeline-chip-pending-approval',
  },
  {
    key: 2,
    label: 'Approved',
    summaryKey: 'approved',
    testId: 'information-requests__pipeline-chip-approved',
  },
  {
    key: -1,
    label: 'Cancelled',
    summaryKey: 'cancelled',
    testId: 'information-requests__pipeline-chip-cancelled',
  },
];
